import { useEffect, useMemo, useState } from 'react';
import { getStoredInversionModels, mergeInversionModels, model3dKind } from '../lib/model-store';
import { datasetsApi, inversionApi } from '../lib/api';
import { productToModel } from '../lib/inversion-parser';
import { recordToEntry } from '../lib/inversion-results';
import { useOrg } from '../contexts/OrgContext';

const MAX_STORED_3D = 50; // stored models drawn in the 3D view (cells are fetched lazily, 3 at a time)
const MODEL_FETCH_CONCURRENCY = 3;

/**
 * Data of the 3D view, shared by the /3d page and the docked map panel: the org's datasets, the
 * stored inversion models of `projectId` / `datasetId` / `modelId` (the /3d?project=&dataset=&model=
 * links) merged with the map session's models, and which of them the camera fits to.
 *
 * `?model=` wins; `?dataset=` focuses the stored models of that survey (the ones the list returned).
 */
export default function useViewer3dModels({ projectId = null, datasetId = null, modelId = null } = {}) {
  const { selectedOrgId } = useOrg();
  const [datasets, setDatasets] = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setDatasetsLoading(true);
    datasetsApi.list({ page: 1, page_size: 100 })
      .then((res) => { if (live) setDatasets(res.data.datasets || []); })
      .catch((error) => console.error('Failed to load data:', error))
      .finally(() => { if (live) setDatasetsLoading(false); });
    return () => { live = false; };
  }, [selectedOrgId]);

  // Stored models: the list (light: meta + `bounds`, no cells) decides which models are drawn; their
  // cells are fetched on demand — the ?model= / ?dataset= ones first, the rest in the background,
  // MODEL_FETCH_CONCURRENCY at a time. A list item without `bounds` (backend older than the
  // frontend) is fetched in full like before.
  const [storedModels, setStoredModels] = useState([]);
  const [progress, setProgress] = useState({ loading: false, loaded: 0, total: 0 });
  useEffect(() => {
    if (!projectId && !datasetId && !modelId) {
      setStoredModels([]); setProgress({ loading: false, loaded: 0, total: 0 });
      return undefined;
    }
    let live = true;
    setStoredModels([]);
    setProgress({ loading: true, loaded: 0, total: 0 });
    (async () => {
      try {
        let items = [];
        if (projectId || datasetId) {
          const params = { ...(projectId ? { project_id: projectId } : {}), ...(datasetId ? { dataset_id: datasetId } : {}) };
          const res = await inversionApi.list(params);
          items = res.data || [];
        }
        const isFocus = (i) => i.id === modelId || (!modelId && datasetId && (i.dataset_id || i.datasetId) === datasetId);
        const ordered = [...items.filter(isFocus), ...items.filter((i) => !isFocus(i))]
          .filter((i) => !i.bounds || i.bounds.drawable !== false) // lists that carry bounds: skip what cannot be drawn
          .slice(0, MAX_STORED_3D);
        const ids = ordered.map((i) => i.id);
        if (modelId && !ids.includes(modelId)) ids.unshift(modelId);
        if (!live) return;
        setProgress({ loading: ids.length > 0, loaded: 0, total: ids.length });
        let next = 0; let done = 0;
        const worker = async () => {
          while (live && next < ids.length) {
            const idx = next; next += 1;
            let entry = null;
            try {
              const res = await inversionApi.get(ids[idx]);
              entry = recordToEntry(res.data, productToModel);
            } catch (err) {
              console.error('Failed to load stored inversion model:', err);
            }
            if (!live) return;
            done += 1;
            if (entry) setStoredModels((prev) => [...prev, entry]);
            setProgress({ loading: done < ids.length, loaded: done, total: ids.length });
          }
        };
        await Promise.all(Array.from({ length: Math.min(MODEL_FETCH_CONCURRENCY, ids.length) }, worker));
      } catch (err) {
        console.error('Failed to load stored inversion models:', err);
        if (live) setProgress((p) => ({ ...p, loading: false }));
      }
    })();
    return () => { live = false; };
  }, [projectId, datasetId, modelId]);
  const storedLoading = progress.loading;

  // Map session models + the stored ones; SubsurfaceViewer3D draws fences (2D lines) and volumes
  // (3D grid / mesh3d), the others are listed as not drawn.
  // Memoised: SubsurfaceViewer3D and TerrainMesh key their origin/extent on this array's identity.
  const fenceModels = useMemo(() => mergeInversionModels(getStoredInversionModels(), storedModels), [storedModels]);
  const shownModels = useMemo(() => fenceModels.filter((f) => model3dKind(f)), [fenceModels]);
  const focusedIds = modelId
    ? [`srv-${modelId}`]
    : datasetId ? storedModels.filter((m) => m.datasetId === datasetId).map((m) => m.id) : [];
  const fitKey = focusedIds.length ? focusedIds.slice().sort().join(',') : null;
  const fitModelIds = useMemo(() => (fitKey ? fitKey.split(',') : null), [fitKey]);

  return { datasets, datasetsLoading, storedModels, storedLoading, loading: progress.loading, loaded: progress.loaded, total: progress.total, fenceModels, shownModels, focusedIds, fitModelIds };
}
